import React, { useState } from 'react';
import { FiSettings, FiChevronDown, FiCamera } from 'react-icons/fi';
import { HiPaintBrush, HiSparkles } from 'react-icons/hi2';
import './BackgroundOptions.css';

/**
 * BackgroundOptions Component
 * Handles background type selection and related inputs
 */
const BackgroundOptions = ({
  backgroundType,
  onBackgroundTypeChange,
  onBackgroundColorChange,
  passportSize,
  onPassportSizeChange,
  customWidth,
  customHeight,
  onCustomWidthChange,
  onCustomHeightChange,
  selectedCountry,
  onCountryChange,
  countriesList
}) => {
  const [colorValue, setColorValue] = useState('#ffffff');

  const handleTypeChange = (type) => {
    onBackgroundTypeChange(type);
  };

  const handleColorChange = (e) => {
    const color = e.target.value;
    setColorValue(color);
    onBackgroundColorChange(color);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <FiSettings className="text-blue-600 dark:text-blue-400" /> Settings
      </h2>

      {/* Background Type */}
      <div className="space-y-3">
        <p className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Background Type</p>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleTypeChange('transparent')}
            className={`py-3 px-4 rounded-xl border-2 transition-all duration-300 flex flex-col items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              backgroundType === 'transparent'
                ? 'border-blue-600 dark:border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 text-blue-700 dark:text-blue-300 shadow-md transform scale-105'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md hover:scale-105 bg-white dark:bg-slate-700'
              }`}
          >
            <HiSparkles className="text-xl" />
            <span className="text-sm font-bold">Transparent</span>
          </button>

          <button
            onClick={() => handleTypeChange('color')}
            className={`py-3 px-4 rounded-xl border-2 transition-all duration-300 flex flex-col items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              backgroundType === 'color'
                ? 'border-blue-600 dark:border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 text-blue-700 dark:text-blue-300 shadow-md transform scale-105'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md hover:scale-105 bg-white dark:bg-slate-700'
              }`}
          >
            <HiPaintBrush className="text-xl" />
            <span className="text-sm font-bold">Solid Color</span>
          </button>
        </div>

        {backgroundType === 'color' && (
          <div className="mt-3 flex items-center gap-3 p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 shadow-md">
            <input
              type="color"
              value={colorValue}
              onChange={handleColorChange}
              className="w-12 h-12 rounded-xl cursor-pointer border-2 border-gray-300 dark:border-gray-600 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            />
            <input
              type="text"
              value={colorValue}
              onChange={handleColorChange}
              placeholder="#FFFFFF"
              className="flex-1 bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono uppercase dark:text-white font-bold"
            />
          </div>
        )}
      </div>

      {/* Preset Sizes */}
      <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-700">
        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Image Size</p>

        {/* Passport Checkbox */}
        <label className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500/50 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 cursor-pointer transition-all duration-300 shadow-sm hover:shadow-md">
          <input
            type="checkbox"
            checked={passportSize}
            onChange={(e) => {
              onPassportSizeChange(e.target.checked);
              if (e.target.checked) {
                onCustomWidthChange('');
                onCustomHeightChange('');
                onCountryChange('');
              }
            }}
            className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
          />
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900 dark:text-white">Standard Passport</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Fixed 600x600 pixels (2x2")</p>
          </div>
          <FiCamera className="text-xl text-gray-400 dark:text-gray-500" />
        </label>

        {/* Country Select */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-400 px-1">Specific Country Requirements</label>
          <div className="relative">
            <select
              value={selectedCountry}
              onChange={(e) => {
                const country = e.target.value;
                onCountryChange(country);
                if (country) {
                  onPassportSizeChange(false);
                  onCustomWidthChange('');
                  onCustomHeightChange('');
                }
              }}
              className="w-full pl-4 pr-10 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm outline-none font-medium shadow-sm hover:shadow-md"
            >
              <option value="">-- Select Country --</option>
              {countriesList.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-400 dark:text-gray-500">
              <FiChevronDown />
            </div>
          </div>
        </div>

        {/* Custom Size */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-400 dark:text-gray-500 px-1">Custom Resolution</label>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 dark:text-gray-500 text-xs font-bold pointer-events-none">W</span>
              <input
                type="number"
                min="1"
                max="10000"
                value={customWidth}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(value) && parseInt(value) > 0)) {
                    onCustomWidthChange(value);
                    if (value) {
                      onPassportSizeChange(false);
                      onCountryChange('');
                    }
                  }
                }}
                placeholder="Width"
                className="w-full pl-8 pr-3 py-2.5 sm:py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm hover:shadow-md transition-all font-medium"
              />
            </div>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 dark:text-gray-500 text-xs font-bold pointer-events-none">H</span>
              <input
                type="number"
                min="1"
                max="10000"
                value={customHeight}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(value) && parseInt(value) > 0)) {
                    onCustomHeightChange(value);
                    if (value) {
                      onPassportSizeChange(false);
                      onCountryChange('');
                    }
                  }
                }}
                placeholder="Height"
                className="w-full pl-8 pr-3 py-2.5 sm:py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm hover:shadow-md transition-all font-medium"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundOptions;


